import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listRentalBookingsAdmin } from "../../rentals/rentalBookingService";
import useTableSearch from "../../../../hooks/useTableSearch";
import Dialog from "../../../dialog/Dialog";
import Pagination from "../../../common/Pagination";
import {
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../../common/AdminPageLayout";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import {
  BookingDetailHeader,
  BookingDetailSection,
  InfoRow,
} from "../BookingDetailUi";
import {
  formatListingDate as formatDate,
  formatListingDateTime as formatDateTime,
  formatListingTime as formatTime,
} from "../../../../utils/formatListingDate";
import { fuzzyIncludesAny } from "../../../../utils/fuzzyMatch";

const PER_PAGE = 10;

const rentalBookingPayments = (b) =>
  (Array.isArray(b?.rental_payments) && b.rental_payments) ||
  (Array.isArray(b?.rentalPayments) && b.rentalPayments) ||
  [];

const rentalPaidTotal = (b) =>
  rentalBookingPayments(b)
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .reduce((sum, p) => sum + (parseFloat(p?.amount) || 0), 0);

const rentalBookingTotal = (b) => parseFloat(b?.total_price) || 0;

const rentalBalance = (b) => {
  const total = rentalBookingTotal(b);
  const paid = rentalPaidTotal(b);
  return Math.max(0, Math.round((total - paid) * 100) / 100);
};

const RentalBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["adminRentalBookings"],
    queryFn: async () => {
      const res = await listRentalBookingsAdmin();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const filtered = useMemo(() => {
    if (!activeSearch) return rows;
    return rows.filter((b) =>
      fuzzyIncludesAny(
        [
          b?.item?.title,
          b?.user?.name,
          b?.user?.email,
          b?.id,
          b?.status,
        ],
        activeSearch
      )
    );
  }, [rows, activeSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    setCurrentPage((p) => Math.min(p, lastPage));
  }, [filtered.length]);

  const pagination = useMemo(() => {
    const total = filtered.length;
    const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
    const page = Math.min(currentPage, lastPage);
    const from = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
    const to = Math.min(page * PER_PAGE, total);
    const start = (page - 1) * PER_PAGE;
    const data = filtered.slice(start, start + PER_PAGE);
    return { currentPage: page, lastPage, perPage: PER_PAGE, total, from, to, data };
  }, [filtered, currentPage]);

  const handlePageChange = (page) => {
    if (page !== pagination.currentPage) setCurrentPage(page);
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
  };

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });

  const getPaymentStatus = (booking) => {
    const balance = rentalBalance(booking);
    const paid = rentalPaidTotal(booking);
    if (balance === 0 && paid > 0) return "Paid";
    if (paid > 0) return "Partially Paid";
    return "Unpaid";
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "Partially Paid":
        return "bg-yellow-100 text-yellow-800";
      case "Unpaid":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const selectedBooking =
    rows.find((b) => b.id === selectedBookingId) ||
    filtered.find((b) => b.id === selectedBookingId);

  const safeText = (value, fallback = "N/A") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string" && value.trim() === "") return fallback;
    return String(value);
  };

  const payments = selectedBooking ? rentalBookingPayments(selectedBooking) : [];

  return (
    <>
      <AdminPageCard>
        <AdminPageCardToolbar>
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search rental bookings..."
            />
            {isError ? (
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-semibold text-primary hover:underline"
              >
                Retry
              </button>
            ) : null}
          </div>
        </AdminPageCardToolbar>

        {isError ? (
          <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error?.response?.data?.message || error?.message || "Failed to load rental bookings."}
          </div>
        ) : null}

        <AdminTable className="shadow-none rounded-none">
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Rental</AdminTableTh>
          <AdminTableTh>Customer</AdminTableTh>
          <AdminTableTh>Start</AdminTableTh>
          <AdminTableTh>End</AdminTableTh>
          <AdminTableTh>Total</AdminTableTh>
          <AdminTableTh>Paid</AdminTableTh>
          <AdminTableTh>Balance</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {isLoading ? (
            <tr>
              <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-500">
                Loading…
              </td>
            </tr>
          ) : pagination.data.length > 0 ? (
            pagination.data.map((booking, idx) => (
              <AdminTableRow key={booking.id}>
                <AdminTableCell>
                  {(pagination.currentPage - 1) * pagination.perPage + idx + 1}
                </AdminTableCell>
                <AdminTableCell nowrap={false}>
                  <div className="font-medium">{booking?.item?.title || "Rental"}</div>
                </AdminTableCell>
                <AdminTableCell>
                  {booking?.user?.name || booking?.user?.email || "Customer"}
                </AdminTableCell>
                <AdminTableCell>{formatDateTime(booking.start_datetime)}</AdminTableCell>
                <AdminTableCell>{formatDateTime(booking.end_datetime)}</AdminTableCell>
                <AdminTableCell>{formatCurrency(rentalBookingTotal(booking))}</AdminTableCell>
                <AdminTableCell>{formatCurrency(rentalPaidTotal(booking))}</AdminTableCell>
                <AdminTableCell>{formatCurrency(rentalBalance(booking))}</AdminTableCell>
                <AdminTableCell>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(
                      getPaymentStatus(booking)
                    )}`}
                  >
                    {getPaymentStatus(booking)}
                  </span>
                </AdminTableCell>
                <TableActionsCell>
                  <ViewDetailsButton onClick={() => handleViewDetails(booking)} />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={10}
              searchQuery={activeSearch}
              emptyMessage="No rental bookings found"
              searchMessage="No rental bookings found matching your search"
              onClearSearch={clearSearch}
            />
          )}
        </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Rental Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-3/4"
          showCloseButton={true}
        >
          <div className="space-y-4">
            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking?.item?.title || "Rental"}
              meta={
                <span className="inline-flex items-center gap-1">
                  <i className="fi fi-rr-calendar text-[10px] text-gray-400" />
                  {formatDateTime(selectedBooking.start_datetime)} –{" "}
                  {formatDateTime(selectedBooking.end_datetime)}
                </span>
              }
              badges={
                <>
                  <span className="rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600">
                    Booking: {safeText(selectedBooking.status)}
                  </span>
                  <span className="rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600">
                    Deposit: {safeText(selectedBooking.deposit_status)}
                  </span>
                  <span className="rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600">
                    Return: {safeText(selectedBooking.return_status)}
                  </span>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(rentalBookingTotal(selectedBooking))}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-2">
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Booking information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Pickup</p>
                      <p className="mt-1">{safeText(selectedBooking.pickup_location)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Drop-off</p>
                      <p className="mt-1">{safeText(selectedBooking.dropoff_location)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total</p>
                      <p className="mt-1">{formatCurrency(rentalBookingTotal(selectedBooking))}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Paid (completed payments)</p>
                      <p className="mt-1">{formatCurrency(rentalPaidTotal(selectedBooking))}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Balance</p>
                      <p className="mt-1">{formatCurrency(rentalBalance(selectedBooking))}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Document</p>
                      <p className="mt-1 break-all">{safeText(selectedBooking.document, "—")}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Pickup km</p>
                      <p className="mt-1">{safeText(selectedBooking.pickup_km, "—")}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Return km</p>
                      <p className="mt-1">{safeText(selectedBooking.return_km, "—")}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Driven km</p>
                      <p className="mt-1">{safeText(selectedBooking.driven_km, "—")}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Payments</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Order ID</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Payment ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {payments.length > 0 ? (
                          payments.map((p) => (
                            <tr key={p.id}>
                              <td className="px-4 py-2 text-sm">{formatCurrency(p.amount)}</td>
                              <td className="px-4 py-2 text-sm capitalize">{safeText(p.status)}</td>
                              <td className="px-4 py-2 text-sm">{safeText(p.payment_method)}</td>
                              <td className="px-4 py-2 text-sm break-all">{safeText(p.order_id)}</td>
                              <td className="px-4 py-2 text-sm break-all">{safeText(p.payment_id, "—")}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                              No payment records for this booking.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Customer</h3>
                  <div className="space-y-2">
                    <InfoRow label="Name" value={selectedBooking.user?.name} />
                    <InfoRow label="Email" value={selectedBooking.user?.email} />
                    <InfoRow label="Phone" value={selectedBooking.user?.phone} />
                    <InfoRow label="User ID" value={selectedBooking.user?.id} />
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Rental item</h3>
                  <div className="space-y-2">
                    <InfoRow label="Title" value={selectedBooking.item?.title} />
                    <InfoRow label="Item ID" value={selectedBooking.item?.id} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      <div className="mt-4">
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          showInfo={true}
        />
      </div>
    </>
  );
};

export default RentalBookings;
